import { inject, injectable } from "inversify";
import maplibregl from "maplibre-gl";
import { PMTiles, Protocol } from "pmtiles";
import { EventDispatcher, type IService, Logger } from "@designboticteam/core";
import {
	type ExpressionStyleDefinition,
	isPmTilesSourceId,
	MapStoreService,
	PmTilesService,
	type PmTilesSource,
	type PmTilesSourceId,
} from "@designboticteam/map";
import { MapLibreInstanceService } from "../../core/MapLibreInstanceService";
import { DisplaySettingsController } from "./handlers/DisplaySettingsController";
import { HoverController } from "./handlers/HoverController";
import {
	buildGlobalStateMultiplier,
	createStyleExpressionFillLayer,
	createStyleExpressionOutlineLayer,
	createStyleFillLayer,
	createStyleOutlineLayer,
} from "./utils";

@injectable()
class PmTilesRenderingService implements IService {
	public readonly serviceName = "PmTilesRenderingService";
	private readonly logger = new Logger(`🌍 ${this.serviceName}`, {
		color: "#ffffff",
		backgroundColor: "#00aa00",
	});

	private isDisposed: boolean = false;

	private pmtilesProtocol = new Protocol();
	private unsubscribeFunctions: Array<() => void> = [];

	private hoverController: HoverController;
	private displaySettingsController: DisplaySettingsController;

	public readonly onFeatureHovered = new EventDispatcher<{ features: Array<maplibregl.MapGeoJSONFeature> }>("PmTilesLayerService:onFeatureHovered");
	public readonly onFeaturePicked = new EventDispatcher<{
		features: Array<maplibregl.MapGeoJSONFeature>;
		lngLat: maplibregl.LngLat;
	}>("PmTilesLayerService:onFeaturePicked");
	public readonly onLayerStackChanged = new EventDispatcher("PmTilesLayerService:onLayerStackChanged");

	/** */
	constructor(
		@inject(MapLibreInstanceService) private mapLibreInstanceService: MapLibreInstanceService,
		@inject(MapStoreService) private mapStoreService: MapStoreService,
		@inject(PmTilesService) private pmTilesService: PmTilesService,
	) {
		this.hoverController = new HoverController({ mapLibreInstanceService: this.mapLibreInstanceService });
		this.displaySettingsController = new DisplaySettingsController({ mapLibreInstanceService: this.mapLibreInstanceService });

		this.logger.log("constructed");
	}

	/** */
	public initialize(): void {
		maplibregl.addProtocol("pmtiles", this.pmtilesProtocol.tile);

		// bind events and subscriptions
		this.unsubscribeFunctions = [
			// mousemove
			this.mapLibreInstanceService.map.on("mousemove", this.handleOnMouseMove).unsubscribe,

			// mouseout
			this.mapLibreInstanceService.map.on("mouseout", this.handleOnMouseOut).unsubscribe,

			// click
			this.mapLibreInstanceService.map.on("click", this.handleOnMouseClick).unsubscribe,

			// sourcedata
			this.mapLibreInstanceService.map.on("sourcedata", this.handleOnSourceData).unsubscribe,

			// sourcedataloading
			this.mapLibreInstanceService.map.on("sourcedataloading", this.handleOnSourceDataLoading).unsubscribe,

			// state.sources.pmTiles
			this.mapStoreService.subscribeToState((state) => state.sources.pmTiles, this.handleOnPmTilesSourcesChange, {
				fireImmediately: true,
			}),
		];
		this.logger.log("initialized");
	}

	/** */
	public dispose(): void {
		this.onFeatureHovered.clear();
		this.onFeaturePicked.clear();
		this.onLayerStackChanged.clear();

		this.isDisposed = true;
		this.unsubscribeFunctions.forEach((fn) => void fn());
		this.unsubscribeFunctions = [];

		for (const sourceId of this.mapStoreService.state.sources.pmTiles.keys()) {
			this.mapLibreInstanceService.removeSource({ sourceId });
		}

		maplibregl.removeProtocol("pmtiles");
		this.logger.log("disposed");
	}

	/** */
	private handleOnMouseMove = (event: maplibregl.MapMouseEvent) => {
		this.hoverController.update(event.point);
		this.onFeatureHovered.dispatch({ features: [...this.hoverController.hoveredFeatures] });
	};

	/** */
	private handleOnMouseOut = () => {
		this.hoverController.clear();
	};

	/** */
	private handleOnMouseClick = (event: maplibregl.MapMouseEvent) => {
		this.onFeaturePicked.dispatch({ features: this.hoverController.hoveredFeatures, lngLat: event.lngLat });
	};

	/**
	 * sourcedata - fires on any source-data change (metadata, each tile, feature-state)
	 */
	private handleOnSourceData = (event: maplibregl.MapSourceDataEvent) => {
		if (isPmTilesSourceId(event.sourceId) === false) {
			return;
		}

		if (event.isSourceLoaded === true) {
			this.pmTilesService.setSourceLoading(event.sourceId, false);
		}
	};

	/**
	 * sourcedataloading - fires whenever a source starts fetching data (initial load, and new tiles on pan/zoom)
	 */
	private handleOnSourceDataLoading = (event: maplibregl.MapSourceDataEvent) => {
		if (isPmTilesSourceId(event.sourceId) === false) {
			return;
		}

		this.pmTilesService.setSourceLoading(event.sourceId, true);
	};

	/**
	 * state.sources.pmTiles - fires whenever a PMTiles source is added, removed, or updated in the global state
	 */
	private handleOnPmTilesSourcesChange = async (state: Map<PmTilesSourceId, PmTilesSource>, prevState: Map<PmTilesSourceId, PmTilesSource>) => {
		for (const [sourceId, source] of state) {
			if (prevState.has(sourceId) === false) {
				// layer is added
				await this.loadPmTilesArchive(source);
			}
		}

		// `loadPmTilesArchive` awaits so this can be after disposed (e.g. React StrictMode mount -> unmount -> mount).
		if (this.isDisposed === true) {
			return;
		}

		for (const [sourceId] of prevState) {
			const isLayerRemoved = state.has(sourceId) === false;

			if (isLayerRemoved) {
				this.mapLibreInstanceService.removeSource({ sourceId });
			}
		}

		for (const [sourceId, source] of state) {
			const prevSource = prevState.get(sourceId);

			if (prevSource === undefined) {
				continue;
			}

			if (prevSource.displaySettings.isVisible !== source.displaySettings.isVisible) {
				// visibility toggled for an already-added layer
				this.displaySettingsController.setVisibility({ sourceId, isVisible: source.displaySettings.isVisible });
			}

			if (prevSource.displaySettings.opacity !== source.displaySettings.opacity) {
				this.displaySettingsController.setOpacity({ sourceId, value: source.displaySettings.opacity });
			}
		}

		// update hoverable layers in HoverHandler
		const allLayers = this.mapLibreInstanceService.map.getLayersOrder();
		const hoverableSources = [...this.mapStoreService.state.sources.pmTiles.values()].filter((x) => x.displaySettings.isHoverable).map((x) => x.id.toString());

		const hoverableLayers: Array<string> = [];

		for (let i = 0; i < allLayers.length; i++) {
			const layerId = allLayers[i];

			if (layerId.includes("pmtiles__") === false) {
				continue;
			}

			const layer = this.mapLibreInstanceService.map.getLayer(layerId);

			if (layer === undefined) {
				continue;
			}

			if (hoverableSources.includes(layer.source) === false) {
				continue;
			}

			hoverableLayers.push(layerId);
		}

		this.hoverController.hoverableLayers = hoverableLayers;
	};

	/** */
	private async loadPmTilesArchive(source: PmTilesSource): Promise<void> {
		try {
			const archive = new PMTiles(source.url);
			const header = await archive.getHeader();
			const meta = (await archive.getMetadata()) as Record<string, unknown>;

			if (this.isDisposed === true) {
				return;
			}

			if (this.mapLibreInstanceService.map.isStyleLoaded() === false) {
				await new Promise<void>((resolve) => {
					this.mapLibreInstanceService.map.once("load", () => resolve());
				});

				// @ts-expect-error -- We need to double check after `await`
				if (this.isDisposed === true) {
					return;
				}
			}

			this.logger.log(`PMTiles loaded: ${source.url} maxZoom=${header.maxZoom} tileType=${header.tileType}`);

			// PMTiles header tileType: 1=MVT (vector), 2-5=PNG/JPEG/WEBP/AVIF (raster)
			const isRaster = header.tileType !== 1;

			if (isRaster) {
				this.addRasterLayer({ source, header });
			} else {
				console.log(`[${source.id}] PMTiles vector_layers:`, meta.vector_layers);
				this.addVectorLayers({ source, header, meta });
			}

			this.pmTilesService.setSourceInitialized(source.id, true);
		} catch (error) {
			this.logger.error(`Failed to load PMTiles ${source.id}: ${error}`);
		}
	}

	/** */
	private addRasterLayer({ source, header }: { source: PmTilesSource; header: { minZoom: number; maxZoom: number } }): void {
		if (source.type !== "raster") {
			throw new Error("Invalid raster PMTiles");
		}

		const { id: sourceId, url } = source;
		const displaySettings = this.mapStoreService.state.sources.pmTiles.get(sourceId)?.displaySettings;
		const isVisible = displaySettings?.isVisible ?? false;
		const displayOpacity = displaySettings?.opacity ?? 1;
		const opacity = "raster" in source ? (source.opacity ?? 1) : 1;

		this.mapLibreInstanceService.map.addSource(sourceId, {
			type: "raster",
			url: `pmtiles://${url}`,
			tileSize: 256,
			minzoom: source.minzoom ?? header.minZoom,
			maxzoom: header.maxZoom,
		});

		// Seed the global-state multiplier before the layer renders, so `["global-state", ...]` resolves.
		this.displaySettingsController.setOpacity({ sourceId, value: displayOpacity });

		this.mapLibreInstanceService.map.addLayer({
			id: `${sourceId}__raster`,
			source: sourceId,
			type: "raster",
			paint: {
				"raster-opacity": buildGlobalStateMultiplier({ value: opacity, stateKey: `opacity__${sourceId}` }),
			},
			layout: { visibility: isVisible ? "visible" : "none" },
		});

		this.onLayerStackChanged.dispatch();

		this.logger.log(`Added raster PMTiles layer: ${sourceId}`);
	}

	/** */
	private addVectorLayers({
		source,
		header,
		meta,
	}: {
		source: PmTilesSource;
		header: { minZoom: number; maxZoom: number };
		meta: Record<string, unknown>;
	}): void {
		if (source.type !== "vector") {
			throw new Error("Invalid vector PMTiles");
		}

		const { id: sourceId, url } = source;

		const displaySettings = this.mapStoreService.state.sources.pmTiles.get(sourceId)?.displaySettings;
		const isVisible = displaySettings?.isVisible ?? false;
		const displayOpacity = displaySettings?.opacity ?? 1;

		this.mapLibreInstanceService.map.addSource(sourceId, {
			type: "vector",
			url: `pmtiles://${url}`,
			minzoom: source.minzoom ?? header.minZoom,
			maxzoom: header.maxZoom,
			promoteId: source.promoteId,
		});

		this.displaySettingsController.setOpacity({ sourceId, value: displayOpacity });

		// `vector_layers` is a PMTiles internal field according the name convention: https://gdal.org/en/stable/drivers/vector/pmtiles.html#pmtiles
		const pmTilesSourceLayers = (meta.vector_layers ?? []) as Array<{ id: string }>;

		if ("style" in source) {
			for (const { id: sourceLayerId } of pmTilesSourceLayers) {
				if (source.style.idle.fill !== undefined) {
					const fillLayer = createStyleFillLayer({
						sourceId,
						sourceLayerId,
						idleFill: source.style.idle.fill,
						matchedFill: source.style.matched?.fill,
						hoverFill: source.style.hover.fill,
						isVisible,
					});

					this.mapLibreInstanceService.map.addLayer(fillLayer);
				}

				if (source.style.idle.outline !== undefined) {
					const outlineLayer = createStyleOutlineLayer({
						sourceId,
						sourceLayerId,
						idleOutline: source.style.idle.outline,
						matchedOutline: source.style.matched?.outline,
						hoverOutline: source.style.hover.outline,
						isVisible,
					});

					this.mapLibreInstanceService.map.addLayer(outlineLayer);
				}
			}
		}

		if ("styleExpression" in source) {
			const { attribute, idle, hover } = source.styleExpression;

			const definesFill = (def: ExpressionStyleDefinition) => Object.values(def.cases).some((d) => d.fill !== undefined) || def.fallback?.fill !== undefined;

			const definesOutline = (def: ExpressionStyleDefinition) =>
				Object.values(def.cases).some((d) => d.outline !== undefined) || def.fallback?.outline !== undefined;

			const hasFill = definesFill(idle) || definesFill(hover);
			const hasOutline = definesOutline(idle) || definesOutline(hover);

			for (const { id: sourceLayerId } of pmTilesSourceLayers) {
				if (hasFill) {
					this.mapLibreInstanceService.map.addLayer(createStyleExpressionFillLayer({ sourceId, sourceLayerId, attribute, idle, hover, isVisible }));
				}

				if (hasOutline) {
					this.mapLibreInstanceService.map.addLayer(createStyleExpressionOutlineLayer({ sourceId, sourceLayerId, attribute, idle, hover, isVisible }));
				}
			}

			this.logger.log(`Added expression-styled PMTiles layer: ${sourceId} (${pmTilesSourceLayers.length} vector layers)`);
		}

		this.onLayerStackChanged.dispatch();

		this.logger.log(`Added vector PMTiles layer: ${sourceId} (${pmTilesSourceLayers.length} vector layers)`);
	}

	/** */
	public reorderLayers(orderedSourceIds: ReadonlyArray<PmTilesSourceId>): void {
		this.displaySettingsController.setOrder(orderedSourceIds);
		this.onLayerStackChanged.dispatch();
	}
}

export { PmTilesRenderingService };
